import {
    Checkbox,
    Chip,
    FormControl,
    FormHelperText,
    Input,
    InputLabel,
    OutlinedInput,
} from '@material-ui/core';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Downshift, {
    GetItemPropsOptions,
    GetMenuPropsOptions,
    GetPropsCommonOptions,
} from 'downshift';
import * as React from 'react';
import ReactDOM from 'react-dom';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export interface ITypeAheadFieldProps<T> {
    multiple?: boolean;
    name: string;
    label: string;
    items?: T[];
    initialValue?: T | T[];
    selectedValue?: T | T[];
    inputId: string;
    required?: boolean;
    fullWidth?: boolean;
    showResultsWhileEmpty?: boolean;
    errorMessage?: string;
    cypressLabel?: string;
    childCypressLabel?: string;
    getItems?: (filter: string) => Promise<void>;
    getName: (item: T) => string;
    getValue: (item: T) => string | null;
    setFieldValue: (field: string, value: any) => void;
    outlined?: boolean;
    hideChips?: boolean;
}

interface IRenderItemsProps<T> {
    childCypressLabel?: string;
    filter: string | null;
    getItemProps: (options: GetItemPropsOptions<any>) => any;
    multiple: boolean;
    highlightedIndex: number | null;
    selectedItem: T;
    showResultsWhileEmpty: boolean;
}

interface IRenderPopperProps<T> extends IRenderItemsProps<T> {
    isOpen: boolean;
    getMenuProps: (
        options?: GetMenuPropsOptions | undefined,
        otherOptions?: GetPropsCommonOptions | undefined
    ) => any;
}

interface IRenderSingleItemProps<T> {
    childCypressLabel?: string;
    item: T;
    highlightedIndex: number | null;
    itemProps: MenuItemProps;
    index: number;
    selectedItem: T;
    multiple: boolean;
}

interface IPopperState<T> {
    popperNode: HTMLElement | null | undefined;
    currentFilter: string;
    allItemsSoFar: T[] | null;
    labelWidth: number;
    downshiftInputValue: string;
}

export default class TypeAheadField<T> extends React.Component<
    ITypeAheadFieldProps<T>,
    IPopperState<T>
> {
    private InputLabelRef: InputLabel | null;
    constructor(props: ITypeAheadFieldProps<T>) {
        super(props);
        this.state = {
            allItemsSoFar: null,
            currentFilter: '',
            downshiftInputValue:
                !props.multiple && props.initialValue && !(props.initialValue instanceof Array)
                    ? props.getName(props.initialValue)
                    : '',
            labelWidth: 0,
            popperNode: null,
        };
    }

    public componentDidMount() {
        if (this.props.outlined) {
            this.setState({
                labelWidth: (ReactDOM.findDOMNode(this.InputLabelRef) as HTMLElement).offsetWidth,
            });
        }
    }

    private setValue(value: any) {
        this.props.setFieldValue(this.props.name, value);
    }

    public onDownshiftChange = (selection: T) => {
        const { multiple, selectedValue, getValue, items } = this.props;
        const changingValue = getValue(selection);

        if (multiple && selectedValue instanceof Array) {
            const selectedItems = selectedValue ? [...selectedValue] : [];
            const selectedItem = selectedItems.find((item) => {
                return getValue(item) === changingValue;
            });
            if (selectedItem) {
                selectedItems.splice(selectedItems.indexOf(selectedItem), 1);
                const selectedItemValues = selectedItems.map((item) => getValue(item));
                this.setValue(selectedItemValues);
            } else {
                const selectedItemValues = selectedItems
                    .concat(selection)
                    .map((item) => getValue(item));
                this.setValue(selectedItemValues);
            }
        } else {
            const foundItem = items!.find((item) => {
                return getValue(item) === changingValue;
            });
            if (foundItem) {
                this.setValue(getValue(foundItem));
            } else {
                this.setValue('');
            }
        }
        this.focus();
    };

    public focus = () => {
        if (this.state.popperNode) {
            this.state.popperNode.focus();
        }
    };

    public getItemsWithFilter(filter = '') {
        if (this.props.items) {
            return this.props.items.filter(
                (item) =>
                    !filter ||
                    this.props
                        .getName(item)
                        .toLowerCase()
                        .includes(filter.toLowerCase())
            );
        }
        return [];
    }

    public handleItemDelete = (item: T) => () => {
        const { selectedValue, getValue } = this.props;
        if (selectedValue instanceof Array) {
            const selectedItems = selectedValue ? [...selectedValue] : [];
            selectedItems.splice(selectedItems.indexOf(item), 1);
            this.setValue(selectedItems.map((x) => getValue(x)));
        }
    };

    public inputOnChange = (inputValue: string) => {
        this.setState({ downshiftInputValue: inputValue });
    };

    public itemToString = (item: T) => {
        if (this.props.getName && item) {
            return this.props.getName(item);
        }
        return '';
    };

    public setPopperNode = (popperNode: HTMLElement) => {
        this.setState({ popperNode });
    };

    public render() {
        const {
            label,
            fullWidth = false,
            required = false,
            multiple = false,
            showResultsWhileEmpty = false,
            errorMessage,
            cypressLabel,
            childCypressLabel,
            getName,
            getValue,
            inputId,
            outlined,
            hideChips = false,
        } = this.props;
        const initialItem = this.props.initialValue;
        const selectItem = this.props.selectedValue;
        return (
            <FormControl
                fullWidth={fullWidth}
                required={required}
                error={errorMessage !== undefined}
                variant={outlined ? 'outlined' : undefined}
            >
                <Downshift
                    inputValue={this.state.downshiftInputValue}
                    onInputValueChange={this.inputOnChange}
                    itemToString={this.itemToString}
                    onChange={this.onDownshiftChange}
                    initialSelectedItem={initialItem || null}
                    selectedItem={selectItem || null}
                >
                    {({
                        clearSelection,
                        isOpen,
                        inputValue,
                        getInputProps,
                        getItemProps,
                        getMenuProps,
                        highlightedIndex,
                        selectedItem,
                    }) => (
                        <div>
                            <InputLabel
                                ref={
                                    outlined
                                        ? (ref) => {
                                              this.InputLabelRef = ref;
                                          }
                                        : undefined
                                }
                                variant={outlined ? 'outlined' : undefined}
                                htmlFor={inputId}
                            >
                                {label}
                            </InputLabel>
                            {outlined ? (
                                <OutlinedInput
                                    id={inputId}
                                    name={name}
                                    labelWidth={this.state.labelWidth}
                                    inputRef={this.setPopperNode}
                                    style={{ width: '100%' }}
                                    data-cy={cypressLabel}
                                    {...getInputProps({
                                        labelWidth: this.state.labelWidth,
                                        onChange: (e) => {
                                            if (e.target.value === '') {
                                                clearSelection();
                                            }
                                        },
                                    })}
                                />
                            ) : (
                                <Input
                                    id={inputId}
                                    {...getInputProps({
                                        onChange: (e) => {
                                            if (e.target.value === '') {
                                                clearSelection();
                                            }
                                        },
                                    })}
                                    name={name}
                                    inputRef={this.setPopperNode}
                                    style={{ width: '100%' }}
                                    data-cy={cypressLabel}
                                />
                            )}
                            {multiple && selectedItem && selectedItem.length > 0 && !hideChips
                                ? [...selectedItem].map((item: T, index: number) => (
                                      <Chip
                                          className="type-ahead-chip"
                                          key={getValue(item) || index}
                                          tabIndex={-1}
                                          label={getName(item)}
                                          onDelete={this.handleItemDelete(item)}
                                      />
                                  ))
                                : null}
                            {this.renderPopper({
                                childCypressLabel,
                                filter: inputValue,
                                getItemProps,
                                getMenuProps,
                                highlightedIndex,
                                isOpen,
                                multiple,
                                selectedItem,
                                showResultsWhileEmpty,
                            })}
                        </div>
                    )}
                </Downshift>
                {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
            </FormControl>
        );
    }

    private debouncedFilterItems = AwesomeDebouncePromise(
        (filter: string, showResultsWhileEmpty: boolean) => {
            if (this.props.getItems && (filter !== '' || showResultsWhileEmpty)) {
                this.props.getItems(filter).then(() => this.setState({ currentFilter: filter }));
            }
        },
        600
    );

    private renderCheckbox = (item: T) => {
        const { selectedValue = Array<T>() } = this.props;
        if (selectedValue instanceof Array) {
            return <Checkbox checked={selectedValue.includes(item)} />;
        }
        return null;
    };

    private renderItems = (renderItemsProps: IRenderItemsProps<T>) => {
        const {
            childCypressLabel,
            filter = '',
            getItemProps,
            multiple,
            highlightedIndex,
            selectedItem,
            showResultsWhileEmpty,
        } = renderItemsProps;
        if (this.props.getItems) {
            if (this.state.currentFilter !== filter) {
                this.debouncedFilterItems(filter, showResultsWhileEmpty);
                return null;
            } else if (this.props.items) {
                return this.props.items.map((item, index) =>
                    this.renderSingleItem({
                        childCypressLabel,
                        highlightedIndex,
                        index,
                        item,
                        itemProps: getItemProps({ item }),
                        multiple,
                        selectedItem,
                    })
                );
            }
        } else if (filter === '' && !showResultsWhileEmpty) {
            return null;
        } else {
            return this.getItemsWithFilter(filter!).map((item, index) =>
                this.renderSingleItem({
                    childCypressLabel,
                    highlightedIndex,
                    index,
                    item,
                    itemProps: getItemProps({ item }),
                    multiple,
                    selectedItem,
                })
            );
        }
        return null;
    };

    private renderPopper = (renderPopperProps: IRenderPopperProps<T>) => {
        const {
            filter,
            getItemProps,
            multiple,
            highlightedIndex,
            selectedItem,
            showResultsWhileEmpty,
            isOpen,
            getMenuProps,
            childCypressLabel,
        } = renderPopperProps;
        return (
            <Popper
                open={isOpen}
                anchorEl={this.state.popperNode}
                placement="bottom"
                modifiers={{
                    preventOverflow: {
                        boundariesElement: 'viewport',
                        enabled: true,
                    },
                }}
                style={{
                    zIndex: 1400,
                }}
            >
                <div {...(isOpen ? getMenuProps({}, { suppressRefError: true }) : {})}>
                    <Paper
                        style={{
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                            overflow: 'auto',
                            width: this.state.popperNode
                                ? this.state.popperNode.clientWidth
                                : undefined,
                        }}
                    >
                        {isOpen
                            ? this.renderItems({
                                  childCypressLabel,
                                  filter: filter as string,
                                  getItemProps,
                                  highlightedIndex,
                                  multiple,
                                  selectedItem,
                                  showResultsWhileEmpty,
                              })
                            : null}
                    </Paper>
                </div>
            </Popper>
        );
    };

    private renderSingleItem = (renderSingleItemProps: IRenderSingleItemProps<T>) => {
        const {
            childCypressLabel,
            item,
            index,
            itemProps,
            highlightedIndex,
            selectedItem,
            multiple,
        } = renderSingleItemProps;
        const isHighlighted = highlightedIndex === index;
        const isSelected =
            item && selectedItem
                ? this.props.getValue(item) === this.props.getValue(selectedItem)
                : false;
        return (
            <MenuItem
                {...itemProps}
                data-cy={childCypressLabel}
                key={this.props.getValue(item) || index}
                selected={isHighlighted}
                style={{ fontWeight: isSelected ? 500 : 400 }}
            >
                {multiple && this.renderCheckbox(item)}
                {this.props.getName(item)}
            </MenuItem>
        );
    };
}



// WEBPACK FOOTER //
// ./src/common/TypeAheadField/TypeAheadField.tsx